/**
 * CustomButton component wraps MUI Button with custom styles and loading state.
 *
 * @module CustomButton
 */
import React from 'react'
import { Button, ButtonProps, CircularProgress, SxProps, Theme } from '@mui/material'

/**
 * Props for CustomButton component.
 * @extends ButtonProps
 * @property {boolean} [isloading] - If true, shows a loading spinner and disables the button.
 */
interface CustomButtonProps extends ButtonProps {
	isLoading?: boolean
}

/**
 * ## ForwardRef component for custom styled MUI Button with loading spinner.
 *
 * - @param {CustomButtonProps} props - Button props and custom loading state.
 * - @param {React.Ref<HTMLButtonElement>} ref - Ref forwarded to the button element.
 * - @returns {JSX.Element} The rendered button, with spinner if loading.
 */
const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>((props, ref) => {
	const { size, variant, isLoading, disabled, color } = props

	// Determine spinner size based on button size
	let spinnerSize = '12px'
	if (size === 'medium') {
		spinnerSize = '14px'
	} else if (size === 'large') {
		spinnerSize = '16px'
	}

	// Custom styles for the button
	let customeStyles: SxProps<Theme> | undefined = {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '14px',
		fontWeight: 600,
		color: color === 'inherit' ? color : '#FFFFFF',
		textTransform: 'inherit',
		background:
			!color || color === 'primary'
				? 'linear-gradient(to right, var(--primary-dark), var(--primary))'
				: `var(--${color ?? 'secondary'})`,
		transition: 'all 0.3s ease-in-out',
		'&:disabled': {
			border: `${variant === 'outlined' ? '1px solid rgba(0, 165, 255, 0.5)' : 'none'}`,
			background: `${variant === 'contained' ? 'rgba(0, 165, 255, 0.12)' : 'none'}`,
			color: 'rgba(0, 165, 255, 0.38)',
		},
		'&:hover': {
			background:
				!color || color === 'primary'
					? 'linear-gradient(to right, var(--primary-dark), var(--primary-dark))'
					: `var(--${color}-dark)`,
			color: color === 'inherit' ? color : '#FFFFFF',
			transition: 'all 0.3s ease-in-out',
		},
		...props.sx,
	}

	// Adjust styles for text or outlined variant
	if (variant === 'text' || variant === 'outlined') {
		customeStyles = {
			...customeStyles,
			color: `var(--${color ?? 'secondary'})`,
			background: 'none',
		}
	}

	// Render loading spinner if isLoading is true
	if (isLoading) {
		return (
			<Button
				ref={ref}
				{...props}
				disabled
				color='primary'
				variant={variant ?? 'contained'}
				size={size ?? 'medium'}
				sx={{
					...customeStyles,
					'&:disabled': {
						background: !variant || variant === 'contained' ? 'rgba(0, 165, 255, 0.12) !important' : 'none',
					},
				}}
			>
				<CircularProgress
					role='progress'
					color='primary'
					size={spinnerSize}
					sx={{
						padding: '4px',
					}}
				/>
			</Button>
		)
	}

	// Render normal button
	return (
		<Button
			ref={ref}
			{...props}
			variant={variant ?? 'contained'}
			size={size ?? 'medium'}
			color={color ?? 'info'}
			disabled={disabled ?? false}
			sx={customeStyles}
		/>
	)
})

/**
 * Display name for debugging purposes.
 */
CustomButton.displayName = 'CustomButton'

export default CustomButton
